import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Checkbox, Collapse, Form, Input, Select, Upload } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AdGroupBean, AdSizeBean, CreativeBean } from 'src/api/api'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { enumView } from 'src/util/Util'
import { env } from '../../../env'
import creativeApi from './CreativeApi'

const FormItem = Form.Item
const Option = Select.Option
const TextArea = Input.TextArea

const formatEnum = CreativeBean.FormatEnum
const attributeEnum = CreativeBean.AttributeEnum

interface Props { }

const formInfoItems: FormDataType[] = [
    FormDataType.AdSizes,
    FormDataType.AdGroups
]

const macros = [
    '{WIN_URL}: Win event URL',
    '{IMPR_URL}: Impression event URL',
    '{CLICK_URL}: Click event URL',
    '{CLICK_URL_ENC}: Click event URL (encoded)',
    '{CAMPAIGN}: Campaign ID',
    '{ADGROUP}: Ad Group ID',
    '{CREATIVE}: Creative ID',
    '{TIMESTAMP}: Timestamp',
    '{IMAGE}: Creative image URL',
    '{VIDEO}: Creative video URL',
]

export const macroItems = [
    {
        key: '1',
        label: 'Supported macros',
        children: macros.map((t, index) => <p key={index}>{t}</p>),
    }
]

export const getFileLabel = (format: CreativeBean.FormatEnum) => {
    switch (format) {
        case formatEnum.Video:
        case formatEnum.VAST: return 'Video file'
        case formatEnum.Audio: return 'Audio file'
        default: return 'Image'
    }
}

const CreativeCreate: React.FC<Props> = () => {

    const [form] = useForm()
    const [error, setError] = useState()
    const navigateTo = useNavigate()

    const [iconUrl, setIconUrl] = useState()
    const [iconToUpload, setIconToUpload] = useState()

    const [fileUrl, setFileUrl] = useState()
    const [fileToUpload, setFileToUpload] = useState()

    const [info, setInfo] = useState({
        adSizes: [],
        adGroups: []
    })

    const [params] = useSearchParams()

    const adGroupId = params.get('adGroupId')
    const copyCreativeId = params.get('copyCreativeId')

    const handleSubmit = async () => {
        const values = await form.validateFields()
        try {
            if (copyCreativeId) copyImages(values)

            await creativeApi.create(values, iconToUpload, fileToUpload)
            goBack()
        } catch (e) {
            setError(e.error)
        }
    }

    const loadData = async () => {
        try {
            const resp = await formDataLoader(...formInfoItems)
            setInfo(resp as any)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const loadCopyCreative = async () => {
        try {
            const response: any = await creativeApi.view(copyCreativeId)
            response.data.name = response.data.name + ' copy'
            form.setFieldsValue(response.data)

            setIconUrl(response.data.nativeData?.icon)
            setFileUrl(response.data.nativeData?.banner || response.data.file)
        } catch (e) {
            setError(e.error)
        }
    }

    useEffect(() => {
        copyCreativeId && loadCopyCreative()
    }, [copyCreativeId])

    const copyImages = (values) => {
        if (format == formatEnum.Native) {
            values.nativeData.icon = iconUrl
            values.nativeData.banner = fileUrl
        } else {
            values.file = fileUrl
        }
    }

    const goBack = () => navigateTo(adGroupId ? `/ad-group/${adGroupId}/edit` : '/creative')

    const format = Form.useWatch('format', form)

    return <Form
        style={{
            maxWidth: '1000px', margin: '0 auto'
        }}
        onFinish={handleSubmit}
        form={form}
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 16 }}
    >
        {error ? <Alert message={error} type="error" /> : null}
        <Card title="Creative" size="small" className="form-card">
            <FormItem
                required
                name="name"
                label="Name"
            >
                <Input placeholder="Name" />
            </FormItem>
            <FormItem
                required
                name="adGroupId"
                label="Ad Group"
                initialValue={adGroupId ? +adGroupId : undefined}
            >
                <Select showSearch optionFilterProp="title" placeholder="Select Ad Group">
                    {info.adGroups.map((item: AdGroupBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="adSizeId"
                label="Ad Size"
            >
                <Select showSearch optionFilterProp="title" placeholder="Select Ad Size">
                    {info.adSizes.map((item: AdSizeBean) => <Option title={item.name} key={item.id} value={item.id}>{item.name}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                required
                name="format"
                label="Format"
            >
                <Select placeholder="Select Format">
                    {Object.values(formatEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="landingUrl"
                label="Landing URL"
            >
                <Input placeholder="Landing URL"/>
            </FormItem>
            <FormItem
                name="attribute"
                label="Attributes"
            >
                <Select mode="multiple" placeholder="Select Attributes" allowClear>
                    {Object.keys(attributeEnum).map(item => <Option key={item} value={item}>{enumView(item)}</Option>)}
                </Select>
            </FormItem>
            <FormItem
                name="active"
                label="Active"
                valuePropName="checked"
                initialValue="true"
                required
            >
                <Checkbox />
            </FormItem>
            {(format == formatEnum.HTML || format == formatEnum.VAST) && <FormItem
                name="adCode"
                label="Ad Code"
            >
                <TextArea rows={4} />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'title']}
                label="Title"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'description']}
                label="Description"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'cta']}
                label="Call to Action"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                name={['nativeData', 'sponsored']}
                label="Sponsored by"
            >
                <Input />
            </FormItem>}
            {format == formatEnum.Native && <FormItem
                label="Icon"
            >
                <Upload
                    maxCount={1}
                    beforeUpload={(incomingFile: any) => {
                        setIconToUpload(incomingFile)
                        return false
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>}
            {format == formatEnum.Native && iconUrl && <FormItem
                wrapperCol={{ offset: 5, span: 16 }}
            >
                <div className="icon-preview">
                    <img src={'' + env.bucket + iconUrl} />
                </div>
            </FormItem>}
            <FormItem
                label={getFileLabel(format)}
            >
                <Upload
                    maxCount={1}
                    beforeUpload={async (incomingFile: any) => {
                        setFileToUpload(incomingFile)

                        const blobUrl = URL.createObjectURL(incomingFile)
                        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                            const i = new Image()
                            i.onload = () => resolve(i)
                            i.onerror = reject
                            i.src = blobUrl
                        })
                        const { width: w, height: h } = img

                        const exist = (info.adSizes as AdSizeBean[]).find((size: AdSizeBean) => size.width == w && size.height == h)
                        if (exist) {
                            form.setFieldValue('adSizeId', exist.id)
                        }

                        return false
                    }}
                >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
            </FormItem>
            {fileUrl && <FormItem
                wrapperCol={{ offset: 5, span: 16 }}
            >
                <div className="creative-preview">
                    <img src={'' + env.bucket + fileUrl} />
                </div>
            </FormItem>}
            {(format == formatEnum.HTML || format == formatEnum.VAST) && <FormItem wrapperCol={{ offset: 5, span: 16 }}>
                <Collapse items={macroItems} size="small" className="macro-guide" />
            </FormItem>}
        </Card>
        <div className="create-cancel-group">
            <Button type="primary" htmlType="submit">Create</Button>
            <Button htmlType="button" danger onClick={goBack}>Cancel</Button>
        </div>
    </Form>
}

export default CreativeCreate


// WEBPACK FOOTER //
// ./src/component/app/creative/CreativeCreate.tsx